import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment, { Moment } from 'moment-timezone';
import { faPen, faBell, faSms, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { Booking } from '@spike/booking-model';
import Client, { ConversionSource, LeadStage } from '@spike/client-model';
import { ClientsStatus, saveClientThunk } from '@spike/clients-action';
import { showSuccessThunk } from '@spike/notifications-action';
import { Pet as PetModel } from '@spike/pet-model';
import { ConfirmDialog, OverFullWindow } from 'components/UI';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import ClientPage from 'pages/Client';
import TwoWaySMSDrawer from 'components/UI/TwoWaySMSDrawer/TwoWaySMSDrawer';
import ShareSendPanel from 'components/Forms/ShareSendPanel';
import SellMembership from 'components/Memberships/Sell/SellMembership';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { RootState } from 'store';
import { TwoWaySmsStatus } from 'reducers/twowaysms/TwoWaySmsState';
import LeadForm from '../Form/LeadForm';
import { useLeadsActions } from '../LeadsProvider';
import LeadDetailHeader from './LeadDetailHeader';
import LeadBookingTab from './Booking/LeadBookingTab';
import LeadFormsTab from './Forms/LeadFormsTab';
import LeadNotesTab from './Notes/LeadNotesTab';
import LeadOverviewTab from './Overview/LeadOverviewTab';
import LeadPetsTab from './Pets/LeadPetsTab';
import LeadTabPlaceholder from './LeadTabPlaceholder';
import { useLeadDetailPermissions } from './useLeadDetailPermissions';
import { DEFAULT_LEAD_DETAIL_TAB, LEAD_DETAIL_TABS, LEAD_DETAIL_TAB_LABELS, LeadDetailTab } from './leadDetailTabs';
import styles from './LeadDetail.module.scss';

const pageBodyId = 'page-content';

interface LeadDetailProps {
    lead: Client;
    onBack: () => void;
}

const LeadDetail: FunctionComponent<LeadDetailProps> = ({ lead, onBack }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();
    const { canCreateSale, canSellMembership } = useLeadDetailPermissions();
    const { refreshLeads, fetchStagesCount } = useLeadsActions();

    const [tab, setTab] = useState<LeadDetailTab>(DEFAULT_LEAD_DETAIL_TAB);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const twoWaySmsStatus = useSelector<RootState, TwoWaySmsStatus>(state => state.twoWaySms.status);
    const savedBooking = useSelector<RootState, Booking | undefined>(state => state.newBookings.bookingSaved);

    const [showEdit, setShowEdit] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [showSms, setShowSms] = useState(false);
    const [showIntakeForm, setShowIntakeForm] = useState(false);
    const [showSellMembership, setShowSellMembership] = useState(false);

    const [intakeFormClosedCount, setIntakeFormClosedCount] = useState(0);
    const [activityRefreshCount, setActivityRefreshCount] = useState(0);

    const [bookingDrawerClosedCount, setBookingDrawerClosedCount] = useState(0);
    const [bookingPetId, setBookingPetId] = useState<number | undefined>(undefined);
    const [addPetForClientId, setAddPetForClientId] = useState<number | undefined>(undefined);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [conversionAt, setConversionAt] = useState<Moment | null>(null);
    const [convertedHere, setConvertedHere] = useState(false);
    const [bookedAt, setBookedAt] = useState<Moment | null>(null);
    const [smsSentAt, setSmsSentAt] = useState<Moment | null>(null);

    const seededNotesLeadIdRef = useRef<number | undefined>(undefined);

    /**
     * A lead moved on from the stage the list is filtered by leaves that list, so the
     * refetched list no longer carries it and the captured lead stays behind. Until the
     * detail is reopened over fresh data, the stages reached here are applied on top.
     */
    const currentLead = useMemo<Client>(() => {
        let result = lead;

        if (smsSentAt !== null && (lead.lastContactedAt == null || smsSentAt.isAfter(lead.lastContactedAt))) {
            result = {
                ...result,
                lastContactedAt: smsSentAt,
                firstContactDate: lead.firstContactDate ?? smsSentAt,
                leadStage: lead.leadStage === LeadStage.New ? LeadStage.Contacted : result.leadStage
            };
        }

        const bookableStage = result.leadStage === LeadStage.New || result.leadStage === LeadStage.Contacted;

        if (bookedAt !== null && lead.firstBookedDate == null) {
            result = {
                ...result,
                firstBookedDate: bookedAt,
                leadStage: bookableStage ? LeadStage.Booked : result.leadStage
            };
        }

        if (convertedHere && conversionAt !== null && lead.clientSince == null) {
            result = {
                ...result,
                clientSince: conversionAt,
                leadStage: LeadStage.Won,
                conversionSource: ConversionSource.Manual
            };
        }

        return result;
    }, [lead, convertedHere, conversionAt, bookedAt, smsSentAt]);

    const refreshActivity = useCallback(() => {
        setActivityRefreshCount(count => count + 1);
    }, []);

    useEffect(() => {
        if (clientsStatus === ClientsStatus.SaveSuccess) {
            refreshActivity();
        }
    }, [clientsStatus, refreshActivity]);

    useEffect(() => {
        if (isConverting && clientsStatus === ClientsStatus.SaveSuccess) {
            setIsConverting(false);
            setShowConvertConfirm(false);
            setConvertedHere(true);
            dispatch(showSuccessThunk('Lead converted to client'));
        }
        if (isConverting && clientsStatus === ClientsStatus.Error) {
            setIsConverting(false);
            setShowConvertConfirm(false);
        }
    }, [isConverting, clientsStatus, dispatch]);

    const handleConvert = useCallback(() => {
        if (currentLead.clientSince != null || isConverting) {
            return;
        }
        const convertedAt = moment();
        setConversionAt(convertedAt);
        setIsConverting(true);
        dispatch(
            saveClientThunk(
                apiClientWrapper,
                {
                    ...currentLead,
                    clientSince: convertedAt,
                    leadStage: LeadStage.Won,
                    conversionSource: ConversionSource.Manual
                },
                false
            )
        );
    }, [currentLead, isConverting, apiClientWrapper, dispatch]);

    const openBooking = useCallback(() => {
        setBookingPetId(undefined);
        setShowBooking(true);
    }, []);

    const closeBooking = useCallback(() => {
        setShowBooking(false);
        setBookingPetId(undefined);
        setBookingDrawerClosedCount(count => count + 1);
    }, []);

    const handleBooked = useCallback(() => {
        closeBooking();
        setBookedAt(savedBooking?.bookedAt?.clone() ?? moment());
        refreshLeads();
        fetchStagesCount();
        refreshActivity();
    }, [closeBooking, savedBooking, refreshLeads, fetchStagesCount, refreshActivity]);

    /**
     * The SMS drawer reports neither sends nor their time, so a send is picked up from
     * the conversation status. It is cleared on the drawer's mount and unmount, so a
     * success can only belong to the drawer currently open.
     */
    useEffect(() => {
        if (showSms && twoWaySmsStatus === TwoWaySmsStatus.SaveSuccess) {
            setSmsSentAt(moment());
        }
    }, [showSms, twoWaySmsStatus]);

    const closeSms = useCallback(() => {
        setShowSms(false);
        if (smsSentAt !== null) {
            refreshLeads();
            fetchStagesCount();
            refreshActivity();
        }
    }, [smsSentAt, refreshLeads, fetchStagesCount, refreshActivity]);

    const handlePetSaved = useCallback(
        (pet: PetModel) => {
            if (pet.id) {
                setBookingPetId(pet.id);
            }
            setAddPetForClientId(undefined);
            refreshActivity();
        },
        [refreshActivity]
    );

    const actions = useMemo<Array<ThreeDotsDropdownOption>>(() => {
        return [
            { id: 'edit', label: 'Edit', icon: faPen, onClick: () => setShowEdit(true) },
            { id: 'book-now', label: 'Book Now', icon: faBell, onClick: openBooking },
            ...(currentLead.phone
                ? [{ id: 'send-sms', label: 'Send SMS', icon: faSms, onClick: () => setShowSms(true) }]
                : []),
            ...(currentLead.token
                ? [
                      {
                          id: 'intake-form',
                          label: 'Send Intake Form',
                          icon: faPaperPlane,
                          onClick: () => setShowIntakeForm(true)
                      }
                  ]
                : [])
        ];
    }, [currentLead, canCreateSale, canSellMembership]);

    return (
        <div className={styles.root}>
            <LeadDetailHeader
                lead={currentLead}
                actions={actions}
                onBack={onBack}
                onConvert={() => setShowConvertConfirm(true)}
            />

            <nav className={styles.tabs}>
                {LEAD_DETAIL_TABS.map(tabId => (
                    <button
                        key={tabId}
                        type="button"
                        className={tabId === tab ? styles.tabActive : styles.tab}
                        onClick={() => setTab(tabId)}
                    >
                        {LEAD_DETAIL_TAB_LABELS[tabId]}
                    </button>
                ))}
            </nav>

            <div className={styles.tabBody}>
                {tab === 'overview' ? (
                    <LeadOverviewTab
                        lead={currentLead}
                        timeZone={timeZone}
                        activityRefreshSignal={activityRefreshCount}
                    />
                ) : tab === 'pets' ? (
                    <LeadPetsTab lead={currentLead} />
                ) : tab === 'forms' ? (
                    <LeadFormsTab
                        lead={currentLead}
                        onEditLead={() => setShowEdit(true)}
                        refreshSignal={intakeFormClosedCount}
                    />
                ) : tab === 'booking' ? (
                    <LeadBookingTab
                        lead={currentLead}
                        onBookAppointment={openBooking}
                        refreshSignal={bookingDrawerClosedCount}
                        scrollableContainerId={pageBodyId}
                    />
                ) : tab === 'notes' ? (
                    <LeadNotesTab
                        lead={currentLead}
                        onNoteAdded={refreshActivity}
                        seededNotesLeadIdRef={seededNotesLeadIdRef}
                    />
                ) : (
                    <LeadTabPlaceholder tabLabel={LEAD_DETAIL_TAB_LABELS[tab]} />
                )}
            </div>

            {showEdit && (
                <LeadForm
                    mode="edit"
                    lead={currentLead}
                    onClose={() => setShowEdit(false)}
                    onSave={updated => {
                        dispatch(saveClientThunk(apiClientWrapper, updated));
                        setShowEdit(false);
                    }}
                    isSaving={false}
                />
            )}

            {showBooking && (
                <CreateBookingDrawer
                    parentID={currentLead.id!}
                    petID={bookingPetId}
                    parentName={`${currentLead.firstName} ${currentLead.lastName}`.trim()}
                    petAdded={bookingPetId !== undefined}
                    showTabs={false}
                    onBooked={handleBooked}
                    onClose={closeBooking}
                    onAddPet={clientId => setAddPetForClientId(clientId)}
                />
            )}

            {addPetForClientId !== undefined &&
                createPortal(
                    <OverFullWindow onClose={() => setAddPetForClientId(undefined)}>
                        <ClientPage
                            hideHeader
                            isNewBooking
                            clientId={addPetForClientId}
                            onClose={() => setAddPetForClientId(undefined)}
                            onClientSaved={() => setAddPetForClientId(undefined)}
                            onPetSaved={handlePetSaved}
                        />
                    </OverFullWindow>,
                    document.body
                )}

            {showSms && (
                <TwoWaySMSDrawer
                    client={currentLead}
                    onClose={closeSms}
                    onBack={closeSms}
                />
            )}

            {showSellMembership && (
                <SellMembership
                    clientId={currentLead.id}
                    onClose={() => setShowSellMembership(false)}
                />
            )}

            <ShareSendPanel
                isOpen={showIntakeForm}
                onClose={() => {
                    setShowIntakeForm(false);
                    setIntakeFormClosedCount(count => count + 1);
                    refreshActivity();
                }}
                token={currentLead.token}
                email={currentLead.email || undefined}
                phone={currentLead.phone || undefined}
            />

            {showConvertConfirm && (
                <ConfirmDialog
                    id="lead_detail_convert_to_client"
                    open={true}
                    isSuccess={true}
                    title="Convert to Client"
                    question={`Convert ${
                        `${currentLead.firstName} ${currentLead.lastName}`.trim() || 'this lead'
                    } to a client?`}
                    subquestion="This action cannot be undone."
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Convert"
                    processing={isConverting}
                    onCancel={() => setShowConvertConfirm(false)}
                    onConfirm={handleConvert}
                />
            )}
        </div>
    );
};

export default LeadDetail;
